import React from "react";

/**
 * In-app upgrade prompt — shown when a feature is gated behind a higher plan.
 *
 * Shows:
 *  - What the feature does (title + description)
 *  - What they get when they upgrade (benefit bullets)
 *  - Which plan includes it
 *  - A clear CTA (mailto or link to billing)
 *
 * Usage:
 *   <UpgradePrompt
 *     featureName="IVR Voice Bot"
 *     plan="Professional"
 *     description="Let callers reach your team 24/7 with a natural-sounding AI phone agent."
 *     benefits={[
 *       "500 AI minutes/month included",
 *       "Handles FAQs, orders, appointments without staff",
 *       "Live bridge transfer to your team when needed",
 *       "Call recordings and transcripts for every call",
 *     ]}
 *   />
 */

interface UpgradePromptProps {
  featureName: string;
  plan?: "Professional" | "Enterprise" | "Add-on";
  description?: string;
  benefits?: string[];
  /** Link to an internal billing/upgrade page if self-serve is available */
  upgradeHref?: string;
  /** mailto subject override */
  subject?: string;
}

const PLAN_BADGE: Record<string, string> = {
  Professional: "bg-blue-50 text-blue-700 border-blue-200",
  Enterprise:   "bg-violet-50 text-violet-700 border-violet-200",
  "Add-on":     "bg-amber-50 text-amber-700 border-amber-200",
};

export function UpgradePrompt({
  featureName,
  plan = "Professional",
  description,
  benefits = [],
  upgradeHref,
  subject,
}: UpgradePromptProps) {
  const mailtoSubject = subject ?? `Upgrade inquiry — ${featureName}`;
  const mailtoHref = `mailto:support@velaro.com?subject=${encodeURIComponent(mailtoSubject)}`;
  const badgeCls = PLAN_BADGE[plan] ?? PLAN_BADGE["Professional"];

  return (
    <div className="flex flex-col items-center justify-center min-h-64 py-16 px-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-zinc-900">{featureName}</h2>
              {description && (
                <p className="text-sm text-zinc-500 mt-1">{description}</p>
              )}
            </div>
            <span className={`shrink-0 text-xs font-medium px-2.5 py-1 rounded-full border ${badgeCls}`}>
              {plan}
            </span>
          </div>
        </div>

        {/* Benefits */}
        {benefits.length > 0 && (
          <div className="px-6 py-4">
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-3">
              What you get
            </p>
            <ul className="space-y-2">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                  <svg className="w-4 h-4 mt-0.5 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="px-6 pb-6 pt-2 flex flex-col sm:flex-row gap-2">
          {upgradeHref ? (
            <a
              href={upgradeHref}
              className="flex-1 text-center px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Upgrade to {plan}
            </a>
          ) : (
            <a
              href={mailtoHref}
              className="flex-1 text-center px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Contact us to upgrade
            </a>
          )}
          <a
            href="https://velaro.com/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center px-4 py-2.5 bg-zinc-100 text-zinc-700 rounded-lg text-sm font-medium hover:bg-zinc-200 transition-colors"
          >
            View pricing
          </a>
        </div>
      </div>
    </div>
  );
}

export default UpgradePrompt;
